import React, { useState, useEffect, useCallback } from 'react'
import {
  PlusIcon,
  FileTextIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  AlertCircleIcon,
  DollarSignIcon,
  ListIcon,
  XIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { JobGeneration } from '../components/JobGeneration'
import { ContractGeneration } from '../components/ContractGeneration'
import { JobLineItemSelector } from '../components/JobLineItemSelector'
import { ContractLineItemPicker } from '../components/ContractLineItemPicker'
import {
  ApartmentTurnoverEditor,
  type ApartmentTurnoverRow,
} from '../components/job/ApartmentTurnoverEditor'
import { JobBillingOptions } from '../components/JobBillingOptions'
import { ActivityTypes, logActivity, logActivityOnLinkedParents } from '../utils/activityLogger'
import { SERVICE_TYPE_OPTIONS } from '../lib/serviceTypeOptions'
import { fetchCrewsAndEmployees } from '../api/jobWorkflow'
import type { EmployeeOption } from '../api/jobWorkflow'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'
import {
  resolveAssignmentDefaultsForJob,
  recordJobAssignmentsOnCreate,
} from '../api/jobAssignmentDefaults'
import type { SelectedContractLineItem } from '../types/job'
import { workSpecsToChecklistItems } from '../lib/jobWorkSpec'
import {
  generateNextJobNumber,
  fallbackJobNumber,
  isDuplicateJobNumberError,
} from '../lib/jobNumbers'
import {
  composeWorkDescriptionFromItems,
  checklistDraftFromItems,
  isRateCardStyleContract,
  resolveWorkItemsForContractLineSelection,
  type ContractServiceWorkSource,
  type ServiceItemWorkFields,
} from '../lib/serviceItemWork'
import { useChecklistTemplates } from '../hooks/useChecklistTemplates'
import { sortTemplatesForServiceType, normalizeChecklistItems } from '../lib/checklistTemplates'
import type { WorkSpecRow } from '../types/agreement'
import { upsertPropertyUnit } from '../api/turnoverContracts'
import { useDivision } from '../contexts/DivisionContext'
import { resolveOperationalDivisionId } from '../lib/operationalDivisions'

interface SelectedLineItem {
  proposal_line_item_id: string
  service_item_id?: string
  description: string
  quantity: number
  is_variable: boolean
  unit_type: string
  unit_price: number
  total_price: number
  estimated_quantity?: number
  max_quantity: number
}

interface BillingOptions {
  billing_type: 'direct' | 'contract' | 'time_materials' | 'included'
  billing_contract_id?: string
  billing_notes?: string
  hourly_rate?: number
  hours_required?: number
}

interface Customer {
  id: string
  company_name?: string
  contact_first_name: string
  contact_last_name: string
}

interface Property {
  id: string
  name: string
  address: string
  city?: string
  state?: string
}

interface ServiceCategory {
  id: string
  name: string
  operational_division_id: string
}

export function JobCreate() {
  const navigate = useNavigate()
  const { divisions } = useDivision()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [showFromProposal, setShowFromProposal] = useState(false)
  const [showFromContract, setShowFromContract] = useState(false)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [properties, setProperties] = useState<Property[]>([])
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)
  const [activeTab, setActiveTab] = useState<
    'overview' | 'schedule' | 'items' | 'billing' | 'work' | 'notes'
  >('overview')
  const [checklistDraft, setChecklistDraft] = useState<
    Array<{ label: string; sort_order: number; photo_required?: boolean }>
  >(
    []
  )
  const { templates: checklistTemplates } = useChecklistTemplates()

  // Day names for recurrence scheduling
  const dayNames = [
    { key: 0, label: 'Sunday' },
    { key: 1, label: 'Monday' },
    { key: 2, label: 'Tuesday' },
    { key: 3, label: 'Wednesday' },
    { key: 4, label: 'Thursday' },
    { key: 5, label: 'Friday' },
    { key: 6, label: 'Saturday' },
  ]

  // Line item selection and billing state
  const [selectedLineItems, setSelectedLineItems] = useState<SelectedLineItem[]>([])
  const [selectedContractLineItems, setSelectedContractLineItems] = useState<SelectedContractLineItem[]>([])
  const [lineItemsTotal, setLineItemsTotal] = useState(0)
  const [contractLineItemsTotal, setContractLineItemsTotal] = useState(0)
  const [billingOptions, setBillingOptions] = useState<BillingOptions>({
    billing_type: 'direct',
  })
  const [hasProposalLineItems, setHasProposalLineItems] = useState(false)
  const [contractLineItemsAvailable, setContractLineItemsAvailable] = useState(0)
  const [quotedAmountOverridden, setQuotedAmountOverridden] = useState(false)
  const [sourceContractIsRecurring, setSourceContractIsRecurring] = useState(false)
  const [contractServicesCache, setContractServicesCache] = useState<ContractServiceWorkSource[]>(
    []
  )
  const [contractIsRateCardStyle, setContractIsRateCardStyle] = useState(false)
  const [apartmentRows, setApartmentRows] = useState<ApartmentTurnoverRow[]>([])
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    service_type: '',
    scheduled_date: '',
    scheduled_start_time: '',
    scheduled_end_time: '',
    estimated_duration: 0,
    priority: 'medium' as 'low' | 'medium' | 'high',
    quoted_amount: 0,
    customer_id: '',
    property_id: '',
    proposal_id: '', // Track source proposal for status update
    contract_id: '',
    notes: '',
    is_recurring: false,
    recurrence_type: 'none' as
      | 'none'
      | 'daily'
      | 'weekly'
      | 'bi_weekly'
      | 'monthly'
      | 'custom_interval',
    recurrence_interval: 1,
    recurrence_days: [] as number[], // For weekly/bi-weekly: days of week (0-6), For monthly: days of month (1-31)
    recurrence_day_of_month: null as number | null, // For monthly: day of month (1-31) - legacy, use recurrence_days array
    recurrence_weekday: null as number | null, // For monthly: day of week (0-6) when using "nth weekday"
    recurrence_occurrence: null as number | null, // For monthly: 1st, 2nd, 3rd, 4th, last (1-5, 5=last)
    recurrence_interval_months: 1, // For custom: every N months
    recurrence_interval_weeks: 1, // For custom: every N weeks
    recurrence_interval_days: 1, // For custom: every N days
    recurrence_end_date: '',
    recurrence_count: 0,
    dsny_integration: false,
    dsny_pickup_days: [] as string[], // ['monday', 'tuesday', etc.]
    dsny_collection_types: [] as string[], // ['garbage', 'recycling', 'organics', 'bulk']
    interior_cleaning_schedule: [] as string[], // Manual interior cleaning schedule
    assigned_crew_id: '',
    assigned_employee_id: '',
    tags: '',
  })
  const [crews, setCrews] = useState<{ id: string; name: string }[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [assignmentTouched, setAssignmentTouched] = useState(false)

  const showItemsTab =
    Boolean(formData.contract_id) ||
    (hasProposalLineItems && Boolean(formData.proposal_id))

  useEffect(() => {
    fetchCustomers()
    fetchServiceCategories()
    fetchCrewsAndEmployees().then(({ crews: c, employees: e }) => {
      setCrews(c)
      setEmployees(e)
    })

    // Check if proposalId is in URL (from ProposalView/ProposalEdit)
    const proposalId = searchParams.get('proposalId')
    if (proposalId) {
      console.log('Loading proposal data for proposalId:', proposalId)
      loadProposalData(proposalId)
    }

    // Check if contractId is in URL (from ContractGeneration)
    const contractId = searchParams.get('contractId')
    if (contractId) {
      console.log('Loading contract data for contractId:', contractId)
      loadContractData(contractId)
    }

    // Prefill from URL (Customers/Properties links, City Records, etc.)
    const cityRecordId = searchParams.get('source_city_record_id')
    const prefillTitle = searchParams.get('title')
    const prefillDesc = searchParams.get('description')
    const prefillProp = searchParams.get('property_id')
    const prefillCust = searchParams.get('customer_id')
    if (cityRecordId || prefillTitle || prefillProp || prefillCust) {
      setFormData((prev) => ({
        ...prev,
        ...(prefillTitle ? { title: prefillTitle } : {}),
        ...(prefillDesc ? { description: prefillDesc } : {}),
        ...(prefillProp ? { property_id: prefillProp } : {}),
        ...(prefillCust ? { customer_id: prefillCust } : {}),
      }))
      if (prefillCust) fetchProperties(prefillCust)
    }
  }, [searchParams])

  // Prefill crew/employee from property / contract defaults until the user edits them.
  useEffect(() => {
    if (assignmentTouched) return
    if (!formData.property_id && !formData.contract_id) return
    let cancelled = false
    ;(async () => {
      try {
        const defaults = await resolveAssignmentDefaultsForJob({
          contractId: formData.contract_id || null,
          propertyId: formData.property_id || null,
        })
        if (cancelled) return
        setFormData((prev) => ({
          ...prev,
          assigned_crew_id: defaults.assigned_crew_id || '',
          assigned_employee_id: defaults.assigned_employee_id || '',
        }))
      } catch (err) {
        console.warn('Could not resolve assignment defaults:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [formData.property_id, formData.contract_id, assignmentTouched])

  const loadProposalData = async (proposalId: string) => {
    try {
      // Fetch proposal with all related data
      const { data: proposal, error } = await supabase
        .from('proposals')
        .select(
          `
          *,
          customer_id,
          property_id
        `
        )
        .eq('id', proposalId)
        .single()

      if (error) throw error

      if (proposal) {
        const serviceType = proposal.service_type || proposal.template_type || ''

        setFormData((prev) => ({
          ...prev,
          title: proposal.title || '',
          description: proposal.description || '',
          service_type: serviceType,
          customer_id: proposal.customer_id || '',
          property_id: proposal.property_id || '',
          proposal_id: proposalId,
          quoted_amount: proposal.total || proposal.total_amount || 0,
          notes: '',
          scheduled_date: proposal.start_date || '',
          is_recurring: proposal.contract_type === 'recurring',
          dsny_integration: proposal.dsny_integration || false,
          dsny_pickup_days: proposal.dsny_pickup_days || [],
          dsny_collection_types: proposal.dsny_collection_types || [],
        }))

        // If customer_id is set, fetch properties
        if (proposal.customer_id) {
          await fetchProperties(proposal.customer_id)
        }

        // Check if proposal has line items
        const { data: lineItems, error: lineItemsError } = await supabase
          .from('proposal_line_items')
          .select('id')
          .eq('proposal_id', proposalId)
          .limit(1)

        if (!lineItemsError && lineItems && lineItems.length > 0) {
          setHasProposalLineItems(true)
          console.log('Proposal has line items - enabling line item selection')
        }
      }
    } catch (error) {
      console.error('Error loading proposal data:', error)
      alert('Error loading proposal data: ' + (error as any).message)
    }
  }

  const loadContractData = async (contractId: string) => {
    try {
      setApartmentRows([])
      console.log('Loading contract data for contractId:', contractId)

      // Fetch contract with all related data including recurrence fields
      const { data: contract, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('id', contractId)
        .single()

      if (error) {
        console.error('Error fetching contract:', error)
        throw error
      }

      console.log('Fetched contract:', contract)

      if (contract) {
        // Also fetch contract_services to get service-level recurrence data if needed
        const { data: contractServices } = await supabase
          .from('contract_services')
          .select('*, service_item:service_items(id, name, work_description, default_checklist)')
          .eq('contract_id', contractId)

        console.log('Fetched contract services:', contractServices)

        const servicesList = (contractServices || []) as ContractServiceWorkSource[]
        setContractServicesCache(servicesList)
        setSelectedContractLineItems([])
        setContractLineItemsTotal(0)

        const rateCardStyle = isRateCardStyleContract(contract)
        setContractIsRateCardStyle(rateCardStyle)

        // Prefer catalogue work description + checklist from linked services.
        // Rate-card / apartment turnovers: defer until the user picks unit size(s).
        let composedDescription = ''
        if (rateCardStyle) {
          setChecklistDraft([])
        } else {
          const workItems: ServiceItemWorkFields[] = []
          for (const cs of servicesList) {
            const raw = cs.service_item
            const item = Array.isArray(raw) ? raw[0] : raw
            if (!item) continue
            workItems.push({
              id: item.id,
              name: item.name || cs.service_name || null,
              work_description: item.work_description,
              default_checklist: item.default_checklist,
            })
          }
          composedDescription = composeWorkDescriptionFromItems(workItems)
          if (workItems.length > 0) {
            const checklist = checklistDraftFromItems(workItems)
            if (checklist.length > 0) {
              setChecklistDraft(checklist)
            }
          } else {
            const workSpecItems = workSpecsToChecklistItems(
              (contract.document_data as any)?.workSpecs as WorkSpecRow[] | undefined
            )
            if (workSpecItems.length > 0) {
              setChecklistDraft(workSpecItems)
            }
          }
        }

        // Use first service's recurrence data if contract doesn't have it, or use contract-level data
        const serviceData =
          contractServices && contractServices.length > 0 ? contractServices[0] : null

        // Map contract recurrence_type to job recurrence_type
        // Contracts use: 'daily', 'weekly', 'bi_weekly', 'monthly', 'custom_interval'
        // Jobs use the same types
        let recurrenceType:
          | 'none'
          | 'daily'
          | 'weekly'
          | 'bi_weekly'
          | 'monthly'
          | 'custom_interval' = 'none'
        const contractRecurrenceType = (
          contract.recurrence_type ||
          serviceData?.recurrence_type ||
          ''
        ).toLowerCase()

        if (contractRecurrenceType) {
          if (
            contractRecurrenceType === 'daily' ||
            contractRecurrenceType === 'weekly' ||
            contractRecurrenceType === 'bi_weekly' ||
            contractRecurrenceType === 'monthly' ||
            contractRecurrenceType === 'custom_interval'
          ) {
            recurrenceType = contractRecurrenceType as
              | 'daily'
              | 'weekly'
              | 'bi_weekly'
              | 'monthly'
              | 'custom_interval'
          } else if (contractRecurrenceType === 'custom') {
            recurrenceType = 'custom_interval'
          }
        }

        // Get recurrence days from contract or service
        const recurrenceDays = contract.recurrence_days || serviceData?.recurrence_days || []

        console.log('Mapped recurrence data:', {
          recurrenceType,
          recurrenceDays,
          is_recurring: contract.is_recurring,
          contractRecurrenceType: contract.recurrence_type,
        })

        console.log('Contract service_type:', contract.service_type)

        // Pre-populate form data from contract
        setFormData((prev) => ({
          ...prev,
          title: contract.title || '',
          description: rateCardStyle
            ? contract.description || ''
            : composedDescription || contract.description || '',
          service_type: contract.service_type || '',
          customer_id: contract.customer_id || '',
          property_id: contract.property_id || '',
          contract_id: contractId,
          proposal_id: contract.proposal_id || '',
          scheduled_date: contract.start_date || new Date().toISOString().split('T')[0],
          quoted_amount: contract.total_amount || 0,
          notes: contract.notes || '',
          // Recurring schedule fields - set is_recurring to true if recurrence_type is set
          is_recurring: recurrenceType !== 'none' || contract.is_recurring || false,
          recurrence_type: recurrenceType,
          recurrence_days: recurrenceDays,
          recurrence_day_of_month: (contract as any).recurrence_day_of_month || null,
          recurrence_weekday: (contract as any).recurrence_weekday || null,
          recurrence_occurrence: (contract as any).recurrence_occurrence || null,
          recurrence_interval_months: (contract as any).recurrence_interval_months || 1,
          recurrence_interval_weeks: (contract as any).recurrence_interval_weeks || 1,
          recurrence_interval_days: (contract as any).recurrence_interval_days || 1,
          recurrence_end_date: contract.recurrence_end_date || '',
          recurrence_count: contract.recurrence_end_count || 0,
          // DSNY fields
          dsny_integration: contract.dsny_integration || serviceData?.dsny_integration || false,
          dsny_pickup_days: contract.dsny_pickup_days || [],
          dsny_collection_types: contract.dsny_collection_types || [],
          interior_cleaning_schedule: contract.interior_cleaning_schedule || [],
        }))

        console.log('Form data updated with contract data')

        const isRecurringContract =
          Boolean(contract.is_recurring) ||
          String(contract.contract_type || '').toLowerCase() === 'recurring' ||
          recurrenceType !== 'none'
        setSourceContractIsRecurring(isRecurringContract)

        setBillingOptions({
          billing_type: 'contract',
          billing_contract_id: contractId,
          billing_notes: `Linked to contract ${contract.contract_number || contractId}`,
        })

        if (contract.customer_id) {
          await fetchProperties(contract.customer_id)
        }
      } else {
        console.warn('No contract data found for contractId:', contractId)
        alert('Contract not found')
      }
    } catch (error) {
      console.error('Error loading contract data:', error)
      alert('Error loading contract data: ' + (error as any).message)
    }
  }

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name, contact_first_name, contact_last_name')
        .order('company_name', { ascending: true })

      if (error) throw error
      setCustomers(data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  const fetchProperties = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, address, city, state')
        .eq('customer_id', customerId)
        .order('name', { ascending: true })

      if (error) throw error
      setProperties(data || [])
    } catch (error) {
      console.error('Error fetching properties:', error)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name, operational_division_id')
        .eq('is_active', true)
        .order('name', { ascending: true })

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  // Check if selected service is in maintenance division
  const isMaintenanceService = () => {
    if (!formData.service_type) return false
    const selectedCategory = serviceCategories.find((cat) => cat.name === formData.service_type)
    return selectedCategory?.operational_division_id === 'aad2c279-31a9-4f3d-bc5e-195f218b38d7' // Maintenance division ID
  }

  const handleCustomerChange = (customerId: string) => {
    setAssignmentTouched(false)
    setFormData({ ...formData, customer_id: customerId, property_id: '' })
    setProperties([])
    if (customerId) {
      fetchProperties(customerId)
    }
  }

  const fetchDSNYPickupSchedule = async (address: string) => {
    // Attempt 1: Proxy server (avoids CORS)
    try {
      const response = await fetch('/api/dsny-schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address }),
      })

      if (response.ok) {
        const data = await response.json()
        if (!data.error) {
          return {
            ...data,
            maintenance_schedules: {
              garbage: getMaintenanceDaysFromPickupDays(data.schedules?.garbage || []),
              recycling: getMaintenanceDaysFromPickupDays(data.schedules?.recycling || []),
              organics: getMaintenanceDaysFromPickupDays(data.schedules?.organics || []),
              bulk: getMaintenanceDaysFromPickupDays(data.schedules?.bulk || []),
            },
            maintenance_days: getMaintenanceDaysFromPickupDays(data.pickup_days || []),
          }
        }
      }
    } catch {
      console.log('DSNY proxy not available, trying direct API...')
    }

    // Attempt 2: Direct DSNY API
    try {
      const directUrl = `https://a827-donatenyc.nyc.gov/DSNYGeoCoder/api/DSNYCollection?address=${encodeURIComponent(address)}`
      const response = await fetch(directUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })

      if (response.ok) {
        const data = await response.json()
        if (data && data.RegularCollectionSchedule) {
          const dayMap: { [key: string]: string } = {
            Monday: 'monday',
            Tuesday: 'tuesday',
            Wednesday: 'wednesday',
            Thursday: 'thursday',
            Friday: 'friday',
            Saturday: 'saturday',
            Sunday: 'sunday',
          }

          const parseScheduleDays = (scheduleStr: string | undefined): string[] => {
            if (!scheduleStr) return []
            return scheduleStr
              .split(',')
              .map((d: string) => dayMap[d.trim()])
              .filter(Boolean)
          }

          const pickupDays = parseScheduleDays(data.RegularCollectionSchedule)
          const today = new Date()
          const dayNames = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
          ]
          let nextPickup = null
          for (let i = 0; i < 7; i++) {
            const checkDate = new Date(today)
            checkDate.setDate(today.getDate() + i)
            if (pickupDays.includes(dayNames[checkDate.getDay()])) {
              nextPickup = checkDate.toISOString().split('T')[0]
              break
            }
          }

          return {
            address,
            pickup_days: pickupDays,
            collection_type: 'refuse',
            zone: data.Zone || 'Unknown',
            next_pickup: nextPickup,
            maintenance_days: getMaintenanceDaysFromPickupDays(pickupDays),
            schedules: {
              garbage: pickupDays,
              recycling: parseScheduleDays(data.RecyclingCollectionSchedule),
              organics: parseScheduleDays(data.OrganicsCollectionSchedule),
              bulk: parseScheduleDays(data.BulkPickupCollectionSchedule),
            },
            data_source: 'Official DSNY API',
            last_updated: new Date().toISOString(),
          }
        }
      }
    } catch (err) {
      console.warn('DSNY direct API failed:', err)
    }

    // No fallback - return null and let the caller show an error
    return null
  }

  const getMaintenanceDaysFromPickupDays = (pickupDays: string[]) => {
    const dayMapping: { [key: string]: string } = {
      monday: 'sunday',
      tuesday: 'monday',
      wednesday: 'tuesday',
      thursday: 'wednesday',
      friday: 'thursday',
      saturday: 'friday',
      sunday: 'saturday',
    }

    return pickupDays.map((day) => dayMapping[day.toLowerCase()] || day)
  }

  // Generate upcoming jobs from recurring contract
  const generateUpcomingJobs = (contractData: any, daysAhead: number = 30) => {
    const upcomingJobs = []
    const today = new Date()

    // Get recurring days
    const recurringDays = contractData.recurrence_days || []
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

    // Generate jobs for each recurring day
    for (let i = 0; i < daysAhead; i++) {
      const checkDate = new Date(today)
      checkDate.setDate(today.getDate() + i)
      const dayOfWeek = checkDate.getDay()

      if (recurringDays.includes(dayOfWeek)) {
        upcomingJobs.push({
          date: checkDate.toISOString().split('T')[0],
          day: dayNames[dayOfWeek],
          tasks: contractData.notes?.includes('Daily Tasks:')
            ? extractTasksFromNotes(contractData.notes, dayNames[dayOfWeek])
            : ['Maintenance Tasks'],
        })
      }
    }

    return upcomingJobs
  }

  // Extract tasks from job notes for a specific day
  const extractTasksFromNotes = (notes: string, day: string) => {
    const tasks = []
    const lines = notes.split('\n')
    let inDailyTasks = false

    for (const line of lines) {
      if (line.includes('Daily Tasks:')) {
        inDailyTasks = true
        continue
      }
      if (inDailyTasks && line.includes(`${day}:`)) {
        const taskLine = line.split(`${day}:`)[1]?.trim()
        if (taskLine) {
          tasks.push(...taskLine.split(',').map((t) => t.trim()))
        }
        break
      }
    }

    return tasks.length > 0 ? tasks : ['Maintenance Tasks']
  }

  const generateRecurringJobs = (baseJob: any, recurrenceData: any) => {
    const jobs = []
    const startDate = new Date(baseJob.scheduled_date)
    let currentDate = new Date(startDate)
    let jobCount = 0
    const hasCount = Number(recurrenceData.recurrence_count) > 0
    const endDate = recurrenceData.recurrence_end_date
      ? new Date(recurrenceData.recurrence_end_date)
      : null
    // Cap at 90 days when neither count nor end date is set (mirrors server generator window)
    const horizonEnd = !hasCount && !endDate
      ? new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000)
      : null
    const maxJobs = hasCount ? Number(recurrenceData.recurrence_count) : 365

    while (jobCount < maxJobs) {
      // Calculate next date based on recurrence type
      if (recurrenceData.recurrence_type === 'daily') {
        // Daily: advance by recurrence_interval_days (default 1)
        const intervalDays = recurrenceData.recurrence_interval_days || 1
        currentDate.setDate(currentDate.getDate() + intervalDays)
      } else if (recurrenceData.recurrence_type === 'weekly') {
        // Weekly: advance by 1 week, but only on selected days
        if (recurrenceData.recurrence_days.length > 0) {
          const nextDate = getNextWeeklyDate(currentDate, recurrenceData.recurrence_days)
          if (!nextDate) break
          currentDate = nextDate
        } else {
          currentDate.setDate(currentDate.getDate() + 7)
        }
      } else if (recurrenceData.recurrence_type === 'bi_weekly') {
        // Bi-weekly: advance by 2 weeks, but only on selected days
        if (recurrenceData.recurrence_days.length > 0) {
          const nextDate = getNextBiWeeklyDate(currentDate, recurrenceData.recurrence_days)
          if (!nextDate) break
          currentDate = nextDate
        } else {
          currentDate.setDate(currentDate.getDate() + 14)
        }
      } else if (recurrenceData.recurrence_type === 'monthly') {
        // Monthly: either days of month or nth weekday
        if (recurrenceData.recurrence_weekday === null) {
          // Days of month
          const nextDate = getNextMonthlyDate(currentDate, recurrenceData.recurrence_days)
          if (!nextDate) break
          currentDate = nextDate
        } else {
          // Nth weekday
          const nextDate = getNextNthWeekdayDate(
            currentDate,
            recurrenceData.recurrence_occurrence || 1,
            recurrenceData.recurrence_weekday
          )
          if (!nextDate) break
          currentDate = nextDate
        }
      } else if (recurrenceData.recurrence_type === 'custom_interval') {
        // Custom interval: days, weeks, or months
        if (recurrenceData.recurrence_interval_days > 1) {
          currentDate.setDate(currentDate.getDate() + recurrenceData.recurrence_interval_days)
        } else if (recurrenceData.recurrence_interval_weeks > 1) {
          currentDate.setDate(currentDate.getDate() + 7 * recurrenceData.recurrence_interval_weeks)
        } else {
          currentDate.setMonth(currentDate.getMonth() + recurrenceData.recurrence_interval_months)
        }

        // If days are specified, find next matching day
        if (recurrenceData.recurrence_days.length > 0) {
          const nextDate = getNextCustomRecurrenceDate(currentDate, recurrenceData.recurrence_days)
          if (!nextDate) break
          currentDate = nextDate
        }
      }

      // Check if we've exceeded the end date or 90-day horizon
      if (endDate && currentDate > endDate) {
        break
      }
      if (horizonEnd && currentDate > horizonEnd) {
        break
      }

      // Create recurring job sibling
      const job: Record<string, unknown> = {
        job_number: `${baseJob.job_number}-${jobCount + 2}`,
        title: baseJob.title,
        description: baseJob.description,
        service_type: baseJob.service_type,
        operational_division_id: baseJob.operational_division_id || null,
        scheduled_date: currentDate.toISOString().split('T')[0],
        scheduled_start_time: baseJob.scheduled_start_time?.trim() || null,
        scheduled_end_time: baseJob.scheduled_end_time?.trim() || null,
        estimated_duration: baseJob.estimated_duration || null,
        status: baseJob.status,
        priority: baseJob.priority,
        quoted_amount: baseJob.quoted_amount,
        customer_id: baseJob.customer_id,
        property_id: baseJob.property_id,
        proposal_id: baseJob.proposal_id,
        contract_id: baseJob.contract_id || null,
        parent_recurring_job_id: baseJob.id,
        notes: baseJob.notes,
        is_recurring: true,
        recurrence_type: baseJob.recurrence_type,
        recurrence_days: baseJob.recurrence_days,
        dsny_integration: baseJob.dsny_integration,
        dsny_pickup_days: baseJob.dsny_pickup_days,
        billing_type: baseJob.billing_type || 'direct',
        billing_contract_id: baseJob.billing_contract_id || null,
        billing_notes: baseJob.billing_notes || null,
        hours_required: baseJob.hours_required || null,
        total_billable_amount: baseJob.total_billable_amount || null,
        assigned_crew_id: baseJob.assigned_crew_id || null,
        assigned_employee_id: baseJob.assigned_employee_id || null,
        tags: baseJob.tags || [],
      }

      jobs.push(job)
      jobCount++
    }

    return jobs
  }

  // Helper function to get next weekly date matching selected days
  const getNextWeeklyDate = (currentDate: Date, recurrenceDays: number[]): Date | null => {
    if (recurrenceDays.length === 0) return null

    const today = new Date(currentDate)
    const dayOfWeek = today.getDay()

    // Find the next occurrence day within the next 7 days
    for (let i = 1; i <= 7; i++) {
      const checkDate = new Date(today)
      checkDate.setDate(today.getDate() + i)
      const checkDayOfWeek = checkDate.getDay()

      if (recurrenceDays.includes(checkDayOfWeek)) {
        return checkDate
      }
    }

    return null
  }

  // Helper function to get next bi-weekly date matching selected days
  const getNextBiWeeklyDate = (currentDate: Date, recurrenceDays: number[]): Date | null => {
    if (recurrenceDays.length === 0) return null

    const today = new Date(currentDate)
    const dayOfWeek = today.getDay()

    // Find the next occurrence day within the next 14 days
    for (let i = 1; i <= 14; i++) {
      const checkDate = new Date(today)
      checkDate.setDate(today.getDate() + i)
      const checkDayOfWeek = checkDate.getDay()

      if (recurrenceDays.includes(checkDayOfWeek)) {
        return checkDate
      }
    }

    return null
  }

  // Helper function to get next monthly date matching selected days of month
  const getNextMonthlyDate = (currentDate: Date, recurrenceDays: number[]): Date | null => {
    if (recurrenceDays.length === 0) return null

    const today = new Date(currentDate)
    const currentDay = today.getDate()
    const currentMonth = today.getMonth()
    const currentYear = today.getFullYear()

    // Find the next occurrence day in current or next month
    const sortedDays = [...recurrenceDays].sort((a, b) => a - b)

    // Check remaining days in current month
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
    for (const day of sortedDays) {
      if (day > currentDay && day <= daysInMonth) {
        return new Date(currentYear, currentMonth, day)
      }
    }

    // Check next month
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear
    const daysInNextMonth = new Date(nextYear, nextMonth + 1, 0).getDate()

    for (const day of sortedDays) {
      if (day <= daysInNextMonth) {
        return new Date(nextYear, nextMonth, day)
      }
    }

    return null
  }

  // Helper function to get next nth weekday date (e.g., 2nd Monday)
  const getNextNthWeekdayDate = (
    currentDate: Date,
    occurrence: number, // 1-4 for 1st-4th, 5 for last
    weekday: number // 0-6 for Sunday-Saturday
  ): Date | null => {
    const today = new Date(currentDate)
    const currentMonth = today.getMonth()
    const currentYear = today.getFullYear()

    // Get the nth weekday of current month
    const nthDate = getNthWeekdayOfMonth(currentYear, currentMonth, occurrence, weekday)

    if (nthDate && nthDate > today) {
      return nthDate
    }

    // Get the nth weekday of next month
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear
    return getNthWeekdayOfMonth(nextYear, nextMonth, occurrence, weekday)
  }

  // Helper function to get nth weekday of a month
  const getNthWeekdayOfMonth = (
    year: number,
    month: number,
    occurrence: number,
    weekday: number
  ): Date | null => {
    if (occurrence === 5) {
      // Last occurrence
      const lastDay = new Date(year, month + 1, 0).getDate()
      for (let day = lastDay; day >= 1; day--) {
        const date = new Date(year, month, day)
        if (date.getDay() === weekday) {
          return date
        }
      }
    } else {
      // 1st, 2nd, 3rd, 4th occurrence
      let count = 0
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day)
        if (date.getDay() === weekday) {
          count++
          if (count === occurrence) {
            return date
          }
        }
      }
    }
    return null
  }

  const getNextCustomRecurrenceDate = (currentDate: Date, recurrenceDays: number[]) => {
    if (recurrenceDays.length === 0) return null

    const today = new Date(currentDate)
    const dayOfWeek = today.getDay()

    // Find the next occurrence day
    for (let i = 1; i <= 7; i++) {
      const checkDate = new Date(today)
      checkDate.setDate(today.getDate() + i)
      const checkDayOfWeek = checkDate.getDay()

      if (recurrenceDays.includes(checkDayOfWeek)) {
        return checkDate
      }
    }

    return null
  }

  // Callback for line item selection changes
  const handleLineItemSelectionChange = useCallback((items: SelectedLineItem[], total: number) => {
    setSelectedLineItems(items)
    setLineItemsTotal(total)
    // Update quoted amount from selected line items
    setFormData((prev) => ({
      ...prev,
      quoted_amount: total,
    }))
  }, [])

  const handleContractLineItemSelectionChange = useCallback(
    (items: SelectedContractLineItem[], total: number) => {
      setSelectedContractLineItems(items)
      setContractLineItemsTotal(total)
      const workItems = contractIsRateCardStyle
        ? resolveWorkItemsForContractLineSelection(items, contractServicesCache)
        : []
      const composed = composeWorkDescriptionFromItems(workItems)
      setFormData((prev) => ({
        ...prev,
        ...(total > 0 && !quotedAmountOverridden ? { quoted_amount: total } : {}),
        ...(composed ? { description: composed } : {}),
      }))
    },
    [contractIsRateCardStyle, contractServicesCache, quotedAmountOverridden]
  )

  const handleApartmentRowsChange = useCallback(
    (rows: ApartmentTurnoverRow[]) => {
      setApartmentRows(
        rows.map((row) => {
          if (!row.tierId || row.checklist.length > 0) return row
          const workItems = resolveWorkItemsForContractLineSelection(
            [
              {
                description: row.tierDescription,
                service_item_id: row.serviceItemId,
                contract_service_id: row.contractServiceId,
              },
            ],
            contractServicesCache
          )
          return { ...row, checklist: checklistDraftFromItems(workItems) }
        })
      )
    },
    [contractServicesCache]
  )

  // Callback for billing options changes
  const handleBillingOptionsChange = useCallback((options: BillingOptions) => {
    setBillingOptions(options)
  }, [])

  // Validation and navigation
  const validateTab = (
    tab: 'overview' | 'schedule' | 'items' | 'billing' | 'work' | 'notes'
  ): boolean => {
    switch (tab) {
      case 'overview':
        return !!(formData.title && formData.customer_id && formData.property_id)
      case 'schedule':
        return !!formData.scheduled_date
      case 'items':
        // Require selection only when there are line items available to pick
        if (showItemsTab) {
          const proposalNeeds =
            Boolean(formData.proposal_id) && hasProposalLineItems
          const contractNeeds =
            Boolean(formData.contract_id) && contractLineItemsAvailable > 0
          if (proposalNeeds || contractNeeds) {
            return selectedLineItems.length > 0 || selectedContractLineItems.length > 0
          }
        }
        return true
      case 'billing':
        return true // Billing has a default, so it's always valid
      case 'work':
        return true
      case 'notes':
        return true // Notes are optional
      default:
        return false
    }
  }

  const handleNext = () => {
    if (!validateTab(activeTab)) {
      if (activeTab === 'items' && showItemsTab) {
        alert('Please select at least one line item')
      } else {
        alert('Please fill in all required fields before continuing')
      }
      return
    }

    // Determine which tabs to show based on context
    const tabs: Array<'overview' | 'schedule' | 'items' | 'billing' | 'work' | 'notes'> = showItemsTab
        ? ['overview', 'items', 'work', 'schedule', 'billing', 'notes']
        : ['overview', 'work', 'schedule', 'billing', 'notes']

    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const tabs: Array<'overview' | 'schedule' | 'items' | 'billing' | 'work' | 'notes'> = showItemsTab
        ? ['overview', 'items', 'work', 'schedule', 'billing', 'notes']
        : ['overview', 'work', 'schedule', 'billing', 'notes']

    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.customer_id || !formData.property_id) {
      alert('Please select a customer and property')
      return
    }
    if (contractIsRateCardStyle) {
      const invalidApartment = apartmentRows.some(
        (row) => !row.unitNumber.trim() || !row.tierId
      )
      const normalizedUnits = apartmentRows.map((row) =>
        row.unitNumber.trim().toLowerCase().replace(/\s+/g, '')
      )
      if (apartmentRows.length === 0 || invalidApartment) {
        alert('Add an apartment number and rate tier for every apartment in this job.')
        setActiveTab('items')
        return
      }
      if (new Set(normalizedUnits).size !== normalizedUnits.length) {
        alert('Each apartment can only be added once per job.')
        setActiveTab('items')
        return
      }
    }

    try {
      setLoading(true)

      const jobNumber = await generateNextJobNumber()
      const notes = formData.notes?.trim() || null
      const contractIdFromForm = formData.contract_id || searchParams.get('contractId') || null
      const billableTotal =
        contractLineItemsTotal ||
        lineItemsTotal ||
        formData.quoted_amount ||
        null
      const quotedAmount = quotedAmountOverridden
        ? formData.quoted_amount
        : billableTotal || formData.quoted_amount || 0

      const baseJobData: Record<string, unknown> = {
        job_number: jobNumber,
        title: formData.title,
        description: formData.description,
        service_type: formData.service_type,
        operational_division_id: resolveOperationalDivisionId(formData.service_type, divisions),
        scheduled_date: formData.scheduled_date || null,
        scheduled_start_time: formData.scheduled_start_time?.trim() || null,
        scheduled_end_time: formData.scheduled_end_time?.trim() || null,
        estimated_duration: formData.estimated_duration || null,
        status: 'draft',
        priority: formData.priority,
        quoted_amount: quotedAmount,
        customer_id: formData.customer_id,
        property_id: formData.property_id,
        proposal_id: formData.proposal_id || null,
        contract_id: contractIdFromForm,
        source_city_record_id: searchParams.get('source_city_record_id') || null,
        notes,
        is_recurring:
          formData.is_recurring && formData.recurrence_type !== 'none' ? true : false,
        recurrence_type:
          formData.is_recurring && formData.recurrence_type !== 'none'
            ? formData.recurrence_type
            : null,
        recurrence_days:
          formData.is_recurring && formData.recurrence_days.length > 0
            ? formData.recurrence_days
            : null,
        dsny_integration: formData.dsny_integration || false,
        dsny_pickup_days:
          formData.dsny_integration && formData.dsny_pickup_days.length > 0
            ? formData.dsny_pickup_days
            : null,
        billing_type: billingOptions.billing_type || 'direct',
        billing_contract_id: billingOptions.billing_contract_id || contractIdFromForm || null,
        billing_notes: billingOptions.billing_notes || null,
        hours_required: billingOptions.hours_required || null,
        total_billable_amount: billableTotal,
        assigned_crew_id: formData.assigned_crew_id || null,
        assigned_employee_id: formData.assigned_employee_id || null,
        tags: formData.tags
          ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : [],
      }

      // Create the main job; retry once with a timestamp number if the
      // sequential number collided with a concurrently created job.
      let { data: mainJob, error: mainJobError } = await supabase
        .from('jobs')
        .insert(baseJobData)
        .select()
        .single()

      if (mainJobError && isDuplicateJobNumberError(mainJobError)) {
        baseJobData.job_number = fallbackJobNumber()
        ;({ data: mainJob, error: mainJobError } = await supabase
          .from('jobs')
          .insert(baseJobData)
          .select()
          .single())
      }

      if (mainJobError) throw mainJobError

      try {
        await recordJobAssignmentsOnCreate(
          mainJob.id,
          formData.assigned_crew_id || null,
          formData.assigned_employee_id || null
        )
      } catch (assignErr) {
        console.warn('Job created but job_assignments insert failed:', assignErr)
      }

      const checklistRows = normalizeChecklistItems(checklistDraft)
      if (checklistRows.length > 0 && mainJob.id) {
        await supabase.from('job_tasks').insert(
          checklistRows.map((item) => ({
            job_id: mainJob.id,
            label: item.label,
            sort_order: item.sort_order,
            source: 'work_spec',
            photo_required: !!item.photo_required,
          }))
        )
      }

      console.log('✅ Job created:', mainJob.id, mainJob.job_number)

      const jobDescription = `Job ${mainJob.job_number} created — ${formData.title || 'Untitled'}`
      await logActivity({
        activity_type: ActivityTypes.JOB_CREATED,
        entity_type: 'job',
        entity_id: mainJob.id,
        description: jobDescription,
        metadata: {
          customer_id: formData.customer_id,
          property_id: formData.property_id,
          job_number: mainJob.job_number,
        },
      })
      await logActivityOnLinkedParents({
        parents: {
          customer_id: formData.customer_id,
          property_id: formData.property_id,
        },
        activity_type: ActivityTypes.JOB_CREATED,
        description: jobDescription,
        metadata: {
          child_entity_type: 'job',
          child_entity_id: mainJob.id,
          job_number: mainJob.job_number,
        },
      })

      // Back-link the city record to this job
      const sourceCityRecordId = searchParams.get('source_city_record_id') || null
      if (sourceCityRecordId && mainJob.id) {
        await supabase
          .from('property_city_records')
          .update({ linked_job_id: mainJob.id })
          .eq('id', sourceCityRecordId)
      }

      // Create job line items if we have selected line items from proposal
      if (selectedLineItems.length > 0 && mainJob.id) {
        const jobLineItemsToInsert = selectedLineItems.map((item, index) => ({
          job_id: mainJob.id,
          proposal_line_item_id: item.proposal_line_item_id,
          service_item_id: item.service_item_id || null,
          description: item.description,
          quantity: item.is_variable ? 0 : item.quantity,
          unit_type: item.unit_type,
          unit_price: item.unit_price,
          total_price: item.is_variable ? 0 : item.total_price,
          is_variable: item.is_variable,
          estimated_quantity: item.estimated_quantity || null,
          is_billable: billingOptions.billing_type !== 'included',
          billing_status:
            billingOptions.billing_type === 'contract' ? 'included_in_contract' : 'pending',
          sort_order: index,
        }))

        const { data: insertedLineItems, error: lineItemsError } = await supabase
          .from('job_line_items')
          .insert(jobLineItemsToInsert)
          .select()

        if (lineItemsError) {
          console.warn('Error creating job line items:', lineItemsError)
        } else {
          console.log('✅ Job line items created:', insertedLineItems?.length)

          // Create allocation records for tracking
          const allocationsToInsert = selectedLineItems.map((item, index) => ({
            proposal_line_item_id: item.proposal_line_item_id,
            job_id: mainJob.id,
            job_line_item_id: insertedLineItems?.[index]?.id,
            allocated_quantity: item.is_variable ? 1 : item.quantity,
          }))

          const { error: allocationsError } = await supabase
            .from('proposal_line_item_allocations')
            .insert(allocationsToInsert)

          if (allocationsError) {
            console.warn('Error creating allocations:', allocationsError)
          } else {
            console.log('✅ Line item allocations created')
          }
        }
      }

      // Contract rate-card line items
      if (selectedContractLineItems.length > 0 && mainJob.id) {
        const contractItems = selectedContractLineItems.map((item, index) => ({
          job_id: mainJob.id,
          contract_line_item_id: item.contract_line_item_id,
          contract_service_id: item.contract_service_id || null,
          service_item_id: item.service_item_id || null,
          description: item.description,
          quantity: item.is_variable ? 0 : item.quantity,
          unit_type: item.unit_type,
          unit_price: item.unit_price,
          total_price: item.is_variable ? 0 : item.total_price,
          is_variable: item.is_variable,
          is_billable: billingOptions.billing_type !== 'included',
          billing_status:
            billingOptions.billing_type === 'contract' ? 'included_in_contract' : 'pending',
          sort_order: index,
        }))

        const { data: insertedContractItems, error: contractItemsError } = await supabase
          .from('job_line_items')
          .insert(contractItems)
          .select('id, contract_line_item_id')

        if (contractItemsError) {
          console.warn('Error creating contract job line items:', contractItemsError)
        } else if (apartmentRows.length > 0) {
          const lineIdByTier = new Map(
            (insertedContractItems || []).map((item) => [item.contract_line_item_id, item.id])
          )
          for (const apartment of apartmentRows) {
            if (!apartment.unitNumber.trim() || !apartment.tierId) continue
            const propertyUnit = await upsertPropertyUnit({
              propertyId: apartment.propertyId || formData.property_id,
              unitNumber: apartment.unitNumber,
              rateDescription: apartment.tierDescription || '',
            })
            const jobLineItemId = lineIdByTier.get(apartment.tierId)
            if (!jobLineItemId) continue
            const { data: unitAllocation, error: unitError } = await supabase
              .from('job_line_item_units')
              .insert({
                job_id: mainJob.id,
                job_line_item_id: jobLineItemId,
                property_unit_id: propertyUnit.id,
                property_id: apartment.propertyId || formData.property_id,
                unit_number_snapshot: apartment.unitNumber.trim(),
                normalized_unit_number: propertyUnit.normalized_unit_number,
                tier_description_snapshot: apartment.tierDescription || '',
                completion_status: 'planned',
              })
              .select('id')
              .single()
            if (unitError) throw unitError
            const apartmentTasks = normalizeChecklistItems(apartment.checklist)
            if (apartmentTasks.length > 0) {
              const { error: taskError } = await supabase.from('job_tasks').insert(
                apartmentTasks.map((task) => ({
                  job_id: mainJob.id,
                  job_line_item_unit_id: unitAllocation.id,
                  label: task.label,
                  sort_order: task.sort_order,
                  source: 'work_spec',
                  photo_required: !!task.photo_required,
                }))
              )
              if (taskError) throw taskError
            }
          }
        }
      }

      // Time & materials hourly line item
      if (
        billingOptions.billing_type === 'time_materials' &&
        billingOptions.hourly_rate &&
        mainJob.id
      ) {
        const hours = billingOptions.hours_required || 0
        const { error: tmError } = await supabase.from('job_line_items').insert({
          job_id: mainJob.id,
          description: 'Labor',
          quantity: hours,
          unit_type: 'per_hour',
          unit_price: billingOptions.hourly_rate,
          total_price: hours * billingOptions.hourly_rate,
          is_variable: !hours,
          is_billable: true,
          billing_status: 'pending',
          sort_order: selectedContractLineItems.length + selectedLineItems.length,
        })
        if (tmError) {
          console.warn('Error creating T&M line item:', tmError)
        }
      }

      // If recurring, generate additional jobs (skip when linked to a recurring contract —
      // those visits are owned by generate_jobs_from_visit_groups)
      if (
        !sourceContractIsRecurring &&
        formData.is_recurring &&
        formData.recurrence_type !== 'none'
      ) {
        const recurringJobs = generateRecurringJobs(mainJob, formData)

        if (recurringJobs.length > 0) {
          const { data: insertedSiblings, error: recurringError } = await supabase
            .from('jobs')
            .insert(recurringJobs)
            .select('id')

          if (recurringError) {
            console.warn('Error creating recurring jobs:', recurringError)
            setNotification({
              type: 'error',
              message: `Main job created, but some recurring jobs failed: ${recurringError.message}`,
            })
            setTimeout(() => setNotification(null), 5000)
          } else {
            if (insertedSiblings?.length) {
              for (const sib of insertedSiblings) {
                try {
                  await recordJobAssignmentsOnCreate(
                    sib.id,
                    formData.assigned_crew_id || null,
                    formData.assigned_employee_id || null
                  )
                } catch {
                  /* soft-fail */
                }
              }
            }
            const checklistRowsForSiblings = normalizeChecklistItems(checklistDraft)
            if (checklistRowsForSiblings.length > 0 && insertedSiblings?.length) {
              const siblingTasks = insertedSiblings.flatMap((sib) =>
                checklistRowsForSiblings.map((item) => ({
                  job_id: sib.id,
                  label: item.label,
                  sort_order: item.sort_order,
                  source: 'work_spec',
                  photo_required: !!item.photo_required,
                }))
              )
              await supabase.from('job_tasks').insert(siblingTasks)
            }
            setNotification({
              type: 'success',
              message: `Job created successfully! Generated ${recurringJobs.length + 1} total jobs.`,
            })
          }
        }
      } else {
        setNotification({ type: 'success', message: 'Job created successfully!' })
      }

      // Update proposal status if this job was created from a proposal
      if (formData.proposal_id) {
        try {
          // First, ensure proposal is approved (if not already)
          const { data: proposal } = await supabase
            .from('proposals')
            .select('status, fully_converted')
            .eq('id', formData.proposal_id)
            .single()

          if (proposal) {
            // Update to approved first if not already approved
            if (proposal.status !== 'approved' && proposal.status !== 'converted') {
              await supabase
                .from('proposals')
                .update({ status: 'approved' })
                .eq('id', formData.proposal_id)
              console.log('✅ Proposal status updated to approved:', formData.proposal_id)
            }

            // Then update to converted (if not already converted)
            if (proposal.status !== 'converted') {
              const { error: proposalUpdateError } = await supabase
                .from('proposals')
                .update({ status: 'converted' })
                .eq('id', formData.proposal_id)

              if (proposalUpdateError) {
                console.warn('Proposal status update error:', proposalUpdateError)
              }

              console.log('✅ Proposal status updated to converted:', formData.proposal_id)
            }
          }
        } catch (proposalError) {
          console.error('Error updating proposal status:', proposalError)
          // Don't fail job creation if proposal update fails
        }
      }

      // Brief pause so the in-app success toast is visible before leaving
      setTimeout(() => {
        setNotification(null)
        if (returnTo) {
          navigate(returnTo)
        } else {
          navigate(`/jobs/details/${mainJob.id}`)
        }
      }, 1500)
    } catch (error) {
      console.error('Error creating job:', error)
      setNotification({
        type: 'error',
        message: 'Failed to create job. Please try again.',
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  if (showFromProposal) {
    return (
      <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8 min-w-0">
        <MobilePageHeader
          sticky
          title="From Proposal"
          subtitle="Generate a job from an approved proposal"
          onBack={() => setShowFromProposal(false)}
          backLabel="Back to Create Job"
        />
        <div className={mobile.surface}>
          <JobGeneration onClose={() => setShowFromProposal(false)} />
        </div>
      </MobilePage>
    )
  }

  if (showFromContract) {
    return (
      <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8 min-w-0">
        <MobilePageHeader
          sticky
          title="From Contract"
          subtitle="Generate a job from a contract"
          onBack={() => setShowFromContract(false)}
          backLabel="Back to Create Job"
        />
        <div className={mobile.surface}>
          <ContractGeneration onClose={() => setShowFromContract(false)} />
        </div>
      </MobilePage>
    )
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8 min-w-0">
        <MobilePageHeader
          sticky
          title="Create Job"
          subtitle="Create a new field service job"
          onBack={() => navigate('/jobs')}
          backLabel="Back to Jobs"
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowFromProposal(true)}
                className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
              >
                <FileTextIcon className="w-4 h-4" />
                <span className="hidden sm:inline">From Proposal</span>
                <span className="sm:hidden">Proposal</span>
              </button>
              <button
                type="button"
                onClick={() => setShowFromContract(true)}
                className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
              >
                <FileTextIcon className="w-4 h-4" />
                <span className="hidden sm:inline">From Contract</span>
                <span className="sm:hidden">Contract</span>
              </button>
            </div>
          }
        />

        {(formData.proposal_id || formData.contract_id) && (
          <div className={`${mobile.surface} px-4 py-3`}>
            {formData.proposal_id && (
              <div className="flex items-start gap-2 text-sm text-blue-800">
                <FileTextIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p>Creating from proposal — details pre-populated.</p>
                  {hasProposalLineItems && (
                    <p className="mt-1 text-xs font-medium text-blue-700">
                      {selectedLineItems.length} line items selected
                    </p>
                  )}
                </div>
              </div>
            )}
            {formData.contract_id && (
              <div className="flex items-start gap-2 text-sm text-green-800">
                <FileTextIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p>Creating from contract — pick rate card items on Line Items.</p>
                  {selectedContractLineItems.length > 0 && (
                    <p className="mt-1 text-xs font-medium text-green-700">
                      {selectedContractLineItems.length} line items selected
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className={`${mobile.surface} sticky top-[3.25rem] z-10 overflow-hidden`}>
          {/* Tab Navigation — select line items before Work so they can populate the checklist. */}
          <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-none bg-white/95 backdrop-blur-md">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            {showItemsTab && (
              <button
                type="button"
                onClick={() => setActiveTab('items')}
                className={`px-4 sm:px-5 min-h-[48px] flex items-center text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'items'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <ListIcon className="w-4 h-4" />
                  <span className="sm:hidden">Items</span>
                  <span className="hidden sm:inline">Line Items</span>
                  {(selectedLineItems.length > 0 || selectedContractLineItems.length > 0) && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                      {selectedLineItems.length || selectedContractLineItems.length}
                    </span>
                  )}
                </div>
              </button>
            )}
            <button
              type="button"
              onClick={() => setActiveTab('work')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'work'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Work
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('schedule')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'schedule'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Schedule
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('billing')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'billing'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <DollarSignIcon className="w-4 h-4 hidden sm:block" />
                Billing
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('notes')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'notes'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Notes
            </button>
          </div>
        </div>

        <div className={mobile.surface}>

          <form onSubmit={handleSubmit}>
            <div className="p-4 sm:p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Job Title and Status */}
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5" />
                      Job Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={mobile.label}>
                          Job Title *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className={mobile.field}
                          placeholder="Enter job title"
                        />
                      </div>
                      <div>
                        <label className={mobile.label}>
                          Status
                        </label>
                        <select
                          value="draft"
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                        >
                          <option value="draft">Draft</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Status will be set when job is created
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer & Property */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <UserIcon className="h-5 w-5" />
                      Customer & Property
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={mobile.label}>
                          Customer *
                        </label>
                        <select
                          required
                          value={formData.customer_id}
                          onChange={(e) => handleCustomerChange(e.target.value)}
                          className={mobile.field}
                        >
                          <option value="">Select customer</option>
                          {customers.map((customer) => (
                            <option key={customer.id} value={customer.id}>
                              {customer.company_name ||
                                `${customer.contact_first_name} ${customer.contact_last_name}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={mobile.label}>
                          Property *
                        </label>
                        <select
                          required
                          value={formData.property_id}
                          onChange={(e) => {
                            setAssignmentTouched(false)
                            setFormData({ ...formData, property_id: e.target.value })
                          }}
                          disabled={!formData.customer_id}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Select property</option>
                          {properties.map((property) => (
                            <option key={property.id} value={property.id}>
                              {property.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Service Type & Priority */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5" />
                      Service Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={mobile.label}>
                          Service Type
                        </label>
                        <select
                          value={formData.service_type}
                          onChange={(e) =>
                            setFormData({ ...formData, service_type: e.target.value })
                          }
                          className={mobile.field}
                        >
                          <option value="">Select service type</option>
                          {SERVICE_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={mobile.label}>
                          Priority
                        </label>
                        <select
                          value={formData.priority}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              priority: e.target.value as 'low' | 'medium' | 'high',
                            })
                          }
                          className={mobile.field}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <UserIcon className="h-5 w-5" />
                      Assignment & Tags
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className={mobile.label}>Crew</label>
                        <select
                          value={formData.assigned_crew_id}
                          onChange={(e) => {
                            setAssignmentTouched(true)
                            setFormData({ ...formData, assigned_crew_id: e.target.value })
                          }}
                          className={mobile.field}
                        >
                          <option value="">Unassigned</option>
                          {crews.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={mobile.label}>Employee</label>
                        <select
                          value={formData.assigned_employee_id}
                          onChange={(e) => {
                            setAssignmentTouched(true)
                            setFormData({ ...formData, assigned_employee_id: e.target.value })
                          }}
                          className={mobile.field}
                        >
                          <option value="">Unassigned</option>
                          {employees.map((e) => (
                            <option key={e.id} value={e.id}>
                              {formatEmployeeLabel(e)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={mobile.label}>Tags</label>
                      <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                        placeholder="comma-separated, e.g. urgent, turnover"
                        className={mobile.field}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Schedule Tab */}
              {activeTab === 'schedule' && (
                <div className="space-y-6">
                  {/* Basic Scheduling */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      Schedule
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className={mobile.label}>
                          Scheduled Date *
                        </label>
                        <input
                          type="date"
                          required
                          value={formData.scheduled_date}
                          onChange={(e) =>
                            setFormData({ ...formData, scheduled_date: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                      <div>
                        <label className={mobile.label}>
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={formData.scheduled_start_time}
                          onChange={(e) =>
                            setFormData({ ...formData, scheduled_start_time: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                      <div>
                        <label className={mobile.label}>
                          End Time
                        </label>
                        <input
                          type="time"
                          value={formData.scheduled_end_time}
                          onChange={(e) =>
                            setFormData({ ...formData, scheduled_end_time: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={mobile.label}>
                        Duration (minutes)
                      </label>
                      <input
                        type="number"
                        min="30"
                        step="30"
                        value={formData.estimated_duration}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            estimated_duration: parseInt(e.target.value) || 0,
                          })
                        }
                        className={mobile.field}
                      />
                    </div>
                  </div>

                  {/* Recurring Schedule */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <ClockIcon className="h-5 w-5" />
                      Recurring Schedule
                    </h3>
                    {sourceContractIsRecurring ? (
                      <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        Recurring visits are generated automatically from this contract&apos;s visit
                        groups. This job will be created as a single visit — no additional sibling
                        jobs will be generated here.
                      </p>
                    ) : (
                      <>
                    <div className="flex items-center gap-3 mb-4">
                      <input
                        type="checkbox"
                        id="is_recurring"
                        checked={formData.is_recurring || formData.recurrence_type !== 'none'}
                        onChange={(e) => {
                          const isRecurring = e.target.checked
                          setFormData({
                            ...formData,
                            is_recurring: isRecurring,
                            // If unchecking, reset to 'none', otherwise keep current type
                            recurrence_type: isRecurring
                              ? formData.recurrence_type === 'none'
                                ? 'weekly'
                                : formData.recurrence_type
                              : 'none',
                          })
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
                        This is a recurring job
                      </label>
                    </div>

                    {(formData.is_recurring || formData.recurrence_type !== 'none') && (
                      <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                        <div>
                          <label className={mobile.label}>
                            Recurrence Type *
                          </label>
                          <select
                            value={formData.recurrence_type}
                            onChange={(e) => {
                              const newType = e.target.value
                              setFormData({
                                ...formData,
                                is_recurring: newType !== 'none',
                                recurrence_type: newType as
                                  | 'none'
                                  | 'daily'
                                  | 'weekly'
                                  | 'bi_weekly'
                                  | 'monthly'
                                  | 'custom_interval',
                                recurrence_days:
                                  newType === 'weekly' || newType === 'bi_weekly'
                                    ? formData.recurrence_days
                                    : [],
                                recurrence_day_of_month: null,
                                recurrence_weekday: null,
                                recurrence_occurrence: null,
                                recurrence_interval_months: 1,
                                recurrence_interval_weeks: 1,
                                recurrence_interval_days: 1,
                              })
                            }}
                            className={mobile.field}
                          >
                            <option value="none">No Recurrence</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="bi_weekly">Bi-Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="custom_interval">Custom Interval</option>
                          </select>
                        </div>

                        {/* Daily: Simple interval selector */}
                        {formData.recurrence_type === 'daily' && (
                          <div>
                            <label className={mobile.label}>
                              Repeat Every
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                value={formData.recurrence_interval_days || 1}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    recurrence_interval_days: parseInt(e.target.value) || 1,
                                  })
                                }
                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <span className="text-sm text-gray-700">
                                {formData.recurrence_interval_days === 1 ? 'day' : 'days'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Job will repeat every{' '}
                              {formData.recurrence_interval_days === 1
                                ? 'day'
                                : `${formData.recurrence_interval_days} days`}
                            </p>
                          </div>
                        )}

                        {/* Weekly & Bi-Weekly: Days of Week */}
                        {(formData.recurrence_type === 'weekly' ||
                          formData.recurrence_type === 'bi_weekly') && (
                          <div>
                            <label className={mobile.label}>
                              Select Days of Week
                            </label>
                                                      <div className="grid grid-cols-7 gap-1.5">
                            {dayNames.map((day) => {
                              const selected = formData.recurrence_days.includes(day.key)
                              return (
                                <button
                                  key={day.key}
                                  type="button"
                                  onClick={() => {
                                    const newDays = selected
                                      ? formData.recurrence_days.filter((d) => d !== day.key)
                                      : [...formData.recurrence_days, day.key]
                                    setFormData({ ...formData, recurrence_days: newDays })
                                  }}
                                  className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                    selected
                                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {day.label.slice(0, 3)}
                                </button>
                              )
                            })}
                          </div>
                          </div>
                        )}

                        {/* Monthly: Multiple Days of Month OR Nth Weekday */}
                        {formData.recurrence_type === 'monthly' && (
                          <div className="space-y-4">
                            <div>
                              <label className={mobile.label}>
                                Schedule Type
                              </label>
                              <select
                                value={
                                  formData.recurrence_weekday === null
                                    ? 'day_of_month'
                                    : 'nth_weekday'
                                }
                                onChange={(e) => {
                                  if (e.target.value === 'day_of_month') {
                                    setFormData({
                                      ...formData,
                                      recurrence_days:
                                        formData.recurrence_days.length > 0
                                          ? formData.recurrence_days
                                          : [1],
                                      recurrence_weekday: null,
                                      recurrence_occurrence: null,
                                    })
                                  } else {
                                    setFormData({
                                      ...formData,
                                      recurrence_days: [],
                                      recurrence_weekday: formData.recurrence_weekday ?? 1,
                                      recurrence_occurrence: formData.recurrence_occurrence ?? 1,
                                    })
                                  }
                                }}
                                className={mobile.field}
                              >
                                <option value="day_of_month">
                                  Days of Month (e.g., 1st, 15th, 30th)
                                </option>
                                <option value="nth_weekday">Nth Weekday (e.g., 2nd Monday)</option>
                              </select>
                            </div>

                            {formData.recurrence_weekday === null ? (
                              <div>
                                <label className={mobile.label}>
                                  Select Days of Month (1-31)
                                </label>
                                <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                    <div key={day} className="flex items-center">
                                      <input
                                        type="checkbox"
                                        id={`monthly_day_${day}`}
                                        checked={formData.recurrence_days.includes(day)}
                                        onChange={(e) => {
                                          const newDays = e.target.checked
                                            ? [...formData.recurrence_days, day]
                                            : formData.recurrence_days.filter((d) => d !== day)
                                          setFormData({ ...formData, recurrence_days: newDays })
                                        }}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <label
                                        htmlFor={`monthly_day_${day}`}
                                        className="ml-2 text-sm text-gray-700"
                                      >
                                        {day}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                  Selected:{' '}
                                  {formData.recurrence_days.length > 0
                                    ? formData.recurrence_days.sort((a, b) => a - b).join(', ')
                                    : 'None'}
                                </p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className={mobile.label}>
                                    Occurrence
                                  </label>
                                  <select
                                    value={formData.recurrence_occurrence || 1}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        recurrence_occurrence: parseInt(e.target.value),
                                      })
                                    }
                                    className={mobile.field}
                                  >
                                    <option value="1">1st</option>
                                    <option value="2">2nd</option>
                                    <option value="3">3rd</option>
                                    <option value="4">4th</option>
                                    <option value="5">Last</option>
                                  </select>
                                </div>
                                <div className="col-span-2">
                                  <label className={mobile.label}>
                                    Day of Week
                                  </label>
                                  <select
                                    value={formData.recurrence_weekday ?? 1}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        recurrence_weekday: parseInt(e.target.value),
                                      })
                                    }
                                    className={mobile.field}
                                  >
                                    {dayNames.map((day) => (
                                      <option key={day.key} value={day.key}>
                                        {day.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Custom Interval: Flexible scheduling */}
                        {formData.recurrence_type === 'custom_interval' && (
                          <div className="space-y-4">
                            <div>
                              <label className={mobile.label}>
                                Repeats Every
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  value={
                                    formData.recurrence_interval_days > 1
                                      ? formData.recurrence_interval_days
                                      : formData.recurrence_interval_weeks > 1
                                        ? formData.recurrence_interval_weeks
                                        : formData.recurrence_interval_months || 1
                                  }
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1
                                    if (formData.recurrence_interval_days > 1) {
                                      setFormData({ ...formData, recurrence_interval_days: val })
                                    } else if (formData.recurrence_interval_weeks > 1) {
                                      setFormData({ ...formData, recurrence_interval_weeks: val })
                                    } else {
                                      setFormData({
                                        ...formData,
                                        recurrence_interval_months: val,
                                      })
                                    }
                                  }}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <select
                                  value={
                                    formData.recurrence_interval_days > 1
                                      ? 'days'
                                      : formData.recurrence_interval_weeks > 1
                                        ? 'weeks'
                                        : 'months'
                                  }
                                  onChange={(e) => {
                                    const val =
                                      formData.recurrence_interval_days > 1
                                        ? formData.recurrence_interval_days
                                        : formData.recurrence_interval_weeks > 1
                                          ? formData.recurrence_interval_weeks
                                          : formData.recurrence_interval_months || 1
                                    if (e.target.value === 'days') {
                                      setFormData({
                                        ...formData,
                                        recurrence_interval_days: val,
                                        recurrence_interval_weeks: 1,
                                        recurrence_interval_months: 1,
                                      })
                                    } else if (e.target.value === 'weeks') {
                                      setFormData({
                                        ...formData,
                                        recurrence_interval_days: 1,
                                        recurrence_interval_weeks: val,
                                        recurrence_interval_months: 1,
                                      })
                                    } else {
                                      setFormData({
                                        ...formData,
                                        recurrence_interval_days: 1,
                                        recurrence_interval_weeks: 1,
                                        recurrence_interval_months: val,
                                      })
                                    }
                                  }}
                                  className={`${mobile.field} flex-1`}
                                >
                                  <option value="days">Day(s)</option>
                                  <option value="weeks">Week(s)</option>
                                  <option value="months">Month(s)</option>
                                </select>
                              </div>
                            </div>

                            {/* Days of week for custom interval */}
                            <div>
                              <label className={mobile.label}>
                                On Days (Optional - leave empty for all days)
                              </label>
                                                        <div className="grid grid-cols-7 gap-1.5">
                            {dayNames.map((day) => {
                              const selected = formData.recurrence_days.includes(day.key)
                              return (
                                <button
                                  key={day.key}
                                  type="button"
                                  onClick={() => {
                                    const newDays = selected
                                      ? formData.recurrence_days.filter((d) => d !== day.key)
                                      : [...formData.recurrence_days, day.key]
                                    setFormData({ ...formData, recurrence_days: newDays })
                                  }}
                                  className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                    selected
                                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {day.label.slice(0, 3)}
                                </button>
                              )
                            })}
                          </div>
                            </div>
                          </div>
                        )}

                        {/* End Conditions */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Date (Optional)
                            </label>
                            <input
                              type="date"
                              value={formData.recurrence_end_date}
                              onChange={(e) =>
                                setFormData({ ...formData, recurrence_end_date: e.target.value })
                              }
                              className={mobile.field}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Number of Occurrences (Optional)
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={formData.recurrence_count}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  recurrence_count: parseInt(e.target.value) || 0,
                                })
                              }
                              className={mobile.field}
                              placeholder="Leave empty for unlimited"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </div>

                  {/* DSNY Integration - Only for Maintenance Services */}
                  {isMaintenanceService() && (
                    <div className="bg-gray-50 p-4 rounded-lg mt-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <AlertCircleIcon className="h-5 w-5" />
                        DSNY Integration
                      </h3>
                      <div className="flex items-center gap-3 mb-4">
                        <input
                          type="checkbox"
                          id="dsny_integration"
                          checked={formData.dsny_integration}
                          onChange={(e) =>
                            setFormData({ ...formData, dsny_integration: e.target.checked })
                          }
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <label
                          htmlFor="dsny_integration"
                          className="text-sm font-medium text-gray-700"
                        >
                          Sync with DSNY pickup schedule
                        </label>
                      </div>

                      {formData.dsny_integration && (
                        <div className="space-y-4 pl-6 border-l-2 border-green-200">
                          {/* Daily Maintenance Schedule */}
                          <div className="space-y-4">
                            <h3 className="text-md font-medium text-gray-800">
                              Daily Maintenance Schedule
                            </h3>
                            <p className="text-sm text-gray-600">
                              Configure what maintenance tasks are needed on which days. Employees
                              will see a single daily schedule showing all tasks for each building.
                            </p>

                            {/* Schedule Preview */}
                            {formData.property_id && (
                              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <h4 className="text-sm font-medium text-blue-800 mb-2">
                                  Schedule Preview
                                </h4>
                                <p className="text-xs text-blue-700">
                                  <strong>How it works:</strong> Employees see one daily job per
                                  building that includes all scheduled tasks for that day.
                                </p>
                                <div className="mt-2 text-xs text-blue-600">
                                  <p>
                                    <strong>Example Daily Job:</strong>
                                  </p>
                                  <p>• Interior Cleaning (if scheduled)</p>
                                  <p>• Garbage prep (if DSNY pickup tomorrow)</p>
                                  <p>• Recycling prep (if DSNY pickup tomorrow)</p>
                                  <p>• Organics prep (if DSNY pickup tomorrow)</p>
                                  <p>• Bulk prep (if DSNY pickup tomorrow)</p>
                                </div>
                              </div>
                            )}

                            {/* DSNY Collection Types */}
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-gray-700">
                                DSNY Collection Types (Auto-scheduled)
                              </h4>
                              <p className="text-xs text-gray-500">
                                Select which collection types to monitor. Jobs will be created the
                                evening before pickup days.
                              </p>
                              <div className="grid grid-cols-2 gap-3">
                                {[
                                  {
                                    key: 'garbage',
                                    label: 'Garbage Collection',
                                    description: 'Regular refuse pickup',
                                  },
                                  {
                                    key: 'recycling',
                                    label: 'Recycling',
                                    description: 'Recyclable materials',
                                  },
                                  {
                                    key: 'organics',
                                    label: 'Organics',
                                    description: 'Food scraps & yard waste',
                                  },
                                  {
                                    key: 'bulk',
                                    label: 'Bulk Items',
                                    description: 'Large items & furniture',
                                  },
                                ].map((type) => (
                                  <div key={type.key} className="flex items-start gap-2">
                                    <input
                                      type="checkbox"
                                      id={`collection_${type.key}`}
                                      checked={formData.dsny_collection_types.includes(type.key)}
                                      onChange={(e) => {
                                        const newTypes = e.target.checked
                                          ? [...formData.dsny_collection_types, type.key]
                                          : formData.dsny_collection_types.filter(
                                              (t) => t !== type.key
                                            )
                                        setFormData({
                                          ...formData,
                                          dsny_collection_types: newTypes,
                                        })
                                      }}
                                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1"
                                    />
                                    <div>
                                      <label
                                        htmlFor={`collection_${type.key}`}
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        {type.label}
                                      </label>
                                      <p className="text-xs text-gray-500">{type.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Manual Interior Cleaning Schedule */}
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-gray-700">
                                Interior Cleaning Schedule (Manual)
                              </h4>
                              <p className="text-xs text-gray-500">
                                Select days for interior cleaning. This will be combined with DSNY
                                tasks on the same day.
                              </p>
                              <div className="grid grid-cols-7 gap-2">
                                {[
                                  { key: 'sunday', label: 'Sun' },
                                  { key: 'monday', label: 'Mon' },
                                  { key: 'tuesday', label: 'Tue' },
                                  { key: 'wednesday', label: 'Wed' },
                                  { key: 'thursday', label: 'Thu' },
                                  { key: 'friday', label: 'Fri' },
                                  { key: 'saturday', label: 'Sat' },
                                ].map((day) => (
                                  <div key={day.key} className="flex flex-col items-center gap-1">
                                    <input
                                      type="checkbox"
                                      id={`interior_${day.key}`}
                                      checked={formData.interior_cleaning_schedule.includes(
                                        day.key
                                      )}
                                      onChange={(e) => {
                                        const newSchedule = e.target.checked
                                          ? [...formData.interior_cleaning_schedule, day.key]
                                          : formData.interior_cleaning_schedule.filter(
                                              (d) => d !== day.key
                                            )
                                        setFormData({
                                          ...formData,
                                          interior_cleaning_schedule: newSchedule,
                                        })
                                      }}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label
                                      htmlFor={`interior_${day.key}`}
                                      className="text-xs text-gray-600"
                                    >
                                      {day.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="bg-green-50 p-4 rounded-lg">
                            <p className="text-sm text-green-800 mb-2">
                              <strong>DSNY Integration:</strong> This fetches{' '}
                              <strong>real collection schedules</strong> from the{' '}
                              <strong>official DSNY API</strong> and automatically schedules
                              maintenance jobs for the <strong>evening before</strong> pickup days.
                            </p>
                            <p className="text-xs text-green-700 mb-2">
                              <strong>How it works:</strong> Address → Official DSNY API →
                              Collection Schedules → Maintenance Schedule
                            </p>
                            <p className="text-xs text-green-600 mb-2">
                              <strong>Collection Types:</strong> Select which collection types to
                              include. Jobs will be scheduled for the evening before any selected
                              collection day.
                            </p>
                            <button
                              type="button"
                              onClick={async () => {
                                if (formData.property_id) {
                                  const property = properties.find(
                                    (p) => p.id === formData.property_id
                                  )
                                  if (property) {
                                    const dsnyData = await fetchDSNYPickupSchedule(property.address)
                                    if (dsnyData) {
                                      // Get selected collection types (default to garbage if none selected)
                                      const selectedTypes =
                                        formData.dsny_collection_types.length > 0
                                          ? formData.dsny_collection_types
                                          : ['garbage']

                                      // Create daily schedule showing what tasks are needed each day
                                      const dailySchedule: {
                                        [key: string]: {
                                          interior_cleaning: boolean
                                          tasks: string[]
                                        }
                                      } = {}

                                      // Initialize all days
                                      const days = [
                                        'sunday',
                                        'monday',
                                        'tuesday',
                                        'wednesday',
                                        'thursday',
                                        'friday',
                                        'saturday',
                                      ]
                                      days.forEach((day) => {
                                        dailySchedule[day] = {
                                          interior_cleaning:
                                            formData.interior_cleaning_schedule.includes(day),
                                          tasks: [],
                                        }
                                      })

                                      // Add DSNY tasks (evening before pickup)
                                      selectedTypes.forEach((type) => {
                                        const pickupDays = dsnyData.schedules?.[type] || []
                                        pickupDays.forEach((pickupDay: string) => {
                                          const maintenanceDay = getMaintenanceDaysFromPickupDays([
                                            pickupDay,
                                          ])[0]
                                          if (dailySchedule[maintenanceDay]) {
                                            dailySchedule[maintenanceDay].tasks.push(
                                              `${type} prep (pickup ${pickupDay})`
                                            )
                                          }
                                        })
                                      })

                                      // Get all days that have any tasks
                                      const activeDays = Object.keys(dailySchedule).filter(
                                        (day) =>
                                          dailySchedule[day].interior_cleaning ||
                                          dailySchedule[day].tasks.length > 0
                                      )

                                      const pickupDays = dsnyData.pickup_days.map((day: string) =>
                                        day.toLowerCase()
                                      )

                                      // Set up recurring schedule for days with any tasks
                                      setFormData({
                                        ...formData,
                                        dsny_pickup_days: pickupDays,
                                        is_recurring: true,
                                        recurrence_type: 'custom_interval',
                                        recurrence_days: activeDays
                                          .map((day) => {
                                            const dayIndex = [
                                              'sunday',
                                              'monday',
                                              'tuesday',
                                              'wednesday',
                                              'thursday',
                                              'friday',
                                              'saturday',
                                            ].indexOf(day.toLowerCase())
                                            return dayIndex
                                          })
                                          .filter((index) => index !== -1),
                                        notes: `${
                                          formData.notes
                                        }\n\nDaily Maintenance Schedule:\n- DSNY Collection Types: ${selectedTypes.join(
                                          ', '
                                        )}\n- DSNY Pickup Days: ${dsnyData.pickup_days.join(
                                          ', '
                                        )}\n- Interior Cleaning Days: ${
                                          formData.interior_cleaning_schedule.join(', ') ||
                                          'None selected'
                                        }\n- Active Days: ${activeDays.join(', ')}\n- Next pickup: ${
                                          dsnyData.next_pickup
                                        }\n\nDaily Tasks:\n${Object.entries(dailySchedule)
                                          .filter(
                                            ([day, schedule]) =>
                                              schedule.interior_cleaning ||
                                              schedule.tasks.length > 0
                                          )
                                          .map(([day, schedule]) => {
                                            const tasks = []
                                            if (schedule.interior_cleaning)
                                              tasks.push('Interior Cleaning')
                                            tasks.push(...schedule.tasks)
                                            return `${day}: ${tasks.join(', ')}`
                                          })
                                          .join('\n')}`.trim(),
                                      })

                                      // Create daily schedule summary for alert
                                      const dailySummary = Object.entries(dailySchedule)
                                        .filter(
                                          ([day, schedule]) =>
                                            schedule.interior_cleaning || schedule.tasks.length > 0
                                        )
                                        .map(([day, schedule]) => {
                                          const tasks = []
                                          if (schedule.interior_cleaning)
                                            tasks.push('Interior Cleaning')
                                          tasks.push(...schedule.tasks)
                                          return `${day}: ${tasks.join(', ')}`
                                        })
                                        .join('\n')

                                      alert(
                                        `Daily Maintenance Schedule Created!\n\nDSNY Collection Types: ${selectedTypes.join(
                                          ', '
                                        )}\n\nDaily Tasks:\n${dailySummary}\n\nEmployees will see one job per day with all tasks for that building.`
                                      )
                                    } else {
                                      alert('Could not fetch DSNY schedule for this address')
                                    }
                                  }
                                } else {
                                  alert('Please select a property first')
                                }
                              }}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            >
                              Fetch DSNY Schedule
                            </button>
                          </div>

                          {formData.dsny_pickup_days.length > 0 && (
                            <div className="space-y-2">
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm text-blue-800">
                                  <strong>DSNY Pickup Days:</strong>{' '}
                                  {formData.dsny_pickup_days.join(', ')}
                                </p>
                              </div>
                              <div className="bg-orange-50 p-3 rounded-lg">
                                <p className="text-sm text-orange-800">
                                  <strong>Maintenance Schedule:</strong>{' '}
                                  {getMaintenanceDaysFromPickupDays(formData.dsny_pickup_days).join(
                                    ', '
                                  )}{' '}
                                  (evening before pickup)
                                </p>
                              </div>
                              <div
                                className={`p-3 rounded-lg border ${
                                  formData.dsny_pickup_days.length > 0
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <p
                                  className={`text-sm ${
                                    formData.dsny_pickup_days.length > 0
                                      ? 'text-green-800'
                                      : 'text-gray-600'
                                  }`}
                                >
                                  <strong>
                                    {formData.dsny_pickup_days.length > 0
                                      ? '✅ Data Source: Official DSNY API'
                                      : 'No DSNY data fetched yet'}
                                  </strong>
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Line Items Tab - Only when creating from proposal with line items */}
              {activeTab === 'items' && showItemsTab && (
                <div className="space-y-6">
                  {formData.proposal_id && hasProposalLineItems && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <ListIcon className="h-5 w-5" />
                        Select Line Items from Proposal
                      </h3>
                      <JobLineItemSelector
                        proposalId={formData.proposal_id}
                        onSelectionChange={handleLineItemSelectionChange}
                        initialSelection={selectedLineItems}
                      />
                    </div>
                  )}
                  {formData.contract_id && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <ListIcon className="h-5 w-5" />
                        Select Line Items from Contract
                      </h3>
                      {contractIsRateCardStyle ? (
                        <ApartmentTurnoverEditor
                          contractId={formData.contract_id}
                          initialPropertyId={formData.property_id}
                          rows={apartmentRows}
                          onRowsChange={handleApartmentRowsChange}
                          onPropertyChange={(propertyId) =>
                            setFormData((prev) => ({ ...prev, property_id: propertyId }))
                          }
                          onAvailableCountChange={setContractLineItemsAvailable}
                          onSelectionChange={handleContractLineItemSelectionChange}
                        />
                      ) : (
                        <ContractLineItemPicker
                          contractId={formData.contract_id}
                          onAvailableCountChange={setContractLineItemsAvailable}
                          onSelectionChange={handleContractLineItemSelectionChange}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <div className="space-y-6">
                  {/* Quoted Amount - Shows selected line items total if from proposal */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <DollarSignIcon className="h-5 w-5" />
                      Job Amount
                    </h3>
                    {selectedLineItems.length > 0 ? (
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm text-blue-800">
                                From selected line items:
                              </span>
                              <p className="text-2xl font-bold text-blue-600">
                                ${lineItemsTotal.toFixed(2)}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-xs text-gray-500">
                                {selectedLineItems.length} items
                              </span>
                              {selectedLineItems.some((i) => i.is_variable) && (
                                <p className="text-xs text-orange-600">* Includes variable items</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className={mobile.label}>
                            Override Amount (optional)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.quoted_amount}
                            onChange={(e) => {
                              setQuotedAmountOverridden(true)
                              setFormData({
                                ...formData,
                                quoted_amount: parseFloat(e.target.value) || 0,
                              })
                            }}
                            className={mobile.field}
                            placeholder="Leave as is to use line items total"
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className={mobile.label}>
                          Quoted Amount
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.quoted_amount}
                          onChange={(e) => {
                            setQuotedAmountOverridden(true)
                            setFormData({
                              ...formData,
                              quoted_amount: parseFloat(e.target.value) || 0,
                            })
                          }}
                          className={mobile.field}
                          placeholder="0.00"
                        />
                      </div>
                    )}
                  </div>

                  {/* Billing Options */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <DollarSignIcon className="h-5 w-5" />
                      Billing Options
                    </h3>
                    <JobBillingOptions
                      customerId={formData.customer_id}
                      proposalId={formData.proposal_id}
                      initialOptions={billingOptions}
                      onOptionsChange={handleBillingOptionsChange}
                    />
                  </div>
                </div>
              )}

              {/* Work Tab — description + checklist (matches Job Details Work) */}
              {activeTab === 'work' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900">Work to be done</h3>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className={mobile.field}
                      placeholder="Enter job description"
                    />
                  </div>
                  {apartmentRows.length > 0 && (
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Apartment checklists
                        </h3>
                        <p className="text-sm text-gray-600">
                          Each apartment has independent tasks and progress.
                        </p>
                      </div>
                      {apartmentRows
                        .filter((row) => row.unitNumber.trim() && row.tierId)
                        .map((row) => (
                          <div
                            key={row.clientId}
                            className="bg-white border border-gray-200 rounded-lg p-4 space-y-2"
                          >
                            <div>
                              <p className="font-medium text-gray-900">
                                Apartment {row.unitNumber}
                              </p>
                              <p className="text-xs text-gray-500">{row.tierDescription}</p>
                            </div>
                            {row.checklist.length === 0 ? (
                              <p className="text-sm text-gray-500">
                                No checklist is configured for this rate tier.
                              </p>
                            ) : (
                              row.checklist.map((item, itemIndex) => (
                                <div
                                  key={`${row.clientId}-${itemIndex}`}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    value={item.label}
                                    onChange={(event) =>
                                      setApartmentRows((current) =>
                                        current.map((apartment) =>
                                          apartment.clientId === row.clientId
                                            ? {
                                                ...apartment,
                                                checklist: apartment.checklist.map(
                                                  (task, taskIndex) =>
                                                    taskIndex === itemIndex
                                                      ? { ...task, label: event.target.value }
                                                      : task
                                                ),
                                              }
                                            : apartment
                                        )
                                      )
                                    }
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                  <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                                    <input
                                      type="checkbox"
                                      checked={!!item.photo_required}
                                      onChange={(event) =>
                                        setApartmentRows((current) =>
                                          current.map((apartment) =>
                                            apartment.clientId === row.clientId
                                              ? {
                                                  ...apartment,
                                                  checklist: apartment.checklist.map(
                                                    (task, taskIndex) =>
                                                      taskIndex === itemIndex
                                                        ? {
                                                            ...task,
                                                            photo_required: event.target.checked,
                                                          }
                                                        : task
                                                  ),
                                                }
                                              : apartment
                                          )
                                        )
                                      }
                                    />
                                    Photo
                                  </label>
                                </div>
                              ))
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {apartmentRows.length > 0 ? 'General job checklist' : 'Checklist'}
                      </h3>
                      {checklistTemplates.length > 0 && (
                        <select
                          defaultValue=""
                          onChange={(e) => {
                            const tpl = sortTemplatesForServiceType(
                              checklistTemplates,
                              formData.service_type
                            ).find((t) => t.id === e.target.value)
                            e.target.value = ''
                            if (tpl) setChecklistDraft(normalizeChecklistItems(tpl.items))
                          }}
                          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                        >
                          <option value="">Apply template…</option>
                          {sortTemplatesForServiceType(
                            checklistTemplates,
                            formData.service_type
                          ).map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      These tasks are saved on the job when you create it. Prefills from the
                      contract work spec when available.
                    </p>
                    {checklistDraft.map((item, idx) => (
                      <div key={idx} className="flex flex-wrap gap-2 items-center">
                        <input
                          value={item.label}
                          onChange={(e) => {
                            const next = [...checklistDraft]
                            next[idx] = { ...next[idx], label: e.target.value }
                            setChecklistDraft(next)
                          }}
                          className="flex-1 min-w-[10rem] px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="Checklist item…"
                        />
                        <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={!!item.photo_required}
                            onChange={(e) => {
                              const next = [...checklistDraft]
                              next[idx] = {
                                ...next[idx],
                                photo_required: e.target.checked,
                              }
                              setChecklistDraft(next)
                            }}
                            className="rounded border-gray-300"
                          />
                          Photo required
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setChecklistDraft(checklistDraft.filter((_, i) => i !== idx))
                          }
                          className="text-red-600 text-sm px-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setChecklistDraft([
                          ...checklistDraft,
                          {
                            label: '',
                            sort_order: checklistDraft.length,
                            photo_required: false,
                          },
                        ])
                      }
                      className="text-sm text-blue-600"
                    >
                      + Add item
                    </button>
                  </div>
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5" />
                      Notes
                    </h3>
                    <div>
                      <label className={mobile.label}>
                        Additional Notes
                      </label>
                      <textarea
                        rows={8}
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className={mobile.field}
                        placeholder="Enter any additional notes for this job..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="native-action-bar flex justify-between gap-2 sm:gap-3 max-w-6xl mx-auto lg:static lg:border lg:border-gray-200 lg:rounded-b-xl">
              <div className="flex gap-2 sm:gap-3">
                {activeTab !== 'overview' && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className={mobile.secondaryBtn}
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/jobs')}
                  className={mobile.secondaryBtn}
                >
                  Cancel
                </button>
              </div>
              <div className="flex gap-2 sm:gap-3">
                {activeTab !== 'notes' ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className={mobile.primaryBtn}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className={mobile.primaryBtn}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="w-4 h-4" />
                        <span className="sm:hidden">Create</span>
                        <span className="hidden sm:inline">Create Job</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* Upcoming Jobs Preview - Only show for recurring jobs */}
          {formData.is_recurring &&
            formData.recurrence_days &&
            formData.recurrence_days.length > 0 && (
              <div className="p-4 sm:p-6 border-t border-gray-100">
                <div className="flex items-center justify-between mb-4 gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Upcoming Jobs Preview</h3>
                  <span className="text-sm text-gray-500 shrink-0">Next 30 days</span>
                </div>

                <div className="space-y-2">
                  {generateUpcomingJobs(formData, 30)
                    .slice(0, 10)
                    .map((job, index) => (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-3 bg-gray-50 rounded-xl border border-gray-200 min-h-[52px]"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {new Date(job.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </div>
                          <div className="text-sm text-gray-600 truncate">{job.tasks.join(', ')}</div>
                        </div>
                        <div className="text-xs text-gray-500">{job.day}</div>
                      </div>
                    ))}

                  {generateUpcomingJobs(formData, 30).length > 10 && (
                    <div className="text-center text-sm text-gray-500 py-2">
                      ... and {generateUpcomingJobs(formData, 30).length - 10} more jobs
                    </div>
                  )}
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This preview shows the recurring schedule. Actual jobs
                    will be created based on this pattern.
                  </p>
                </div>
              </div>
            )}
        </div>

      {notification && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div
            className={`relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-md w-full mx-0 sm:mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-medium ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
